import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { holdInUtc, PushProcessingNotification } from "@repo/helpers";
import { ActivableFilterItem, FilterItem, KeyedArray, keyPair } from "@repo/models";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import { useFilterItemQuery } from "../../../api/queries";
import * as Queries from "../../../api/queries";
import { useRepApi, useRepOnboardingQuery } from "../../../api/repApi";
import { useRepSegmentsApi } from "../../../api/repSegmentsApi";
import { useTrainingApi } from "../../../api/trainingApi";
import { EditableItemDynamicTab } from "../../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../../components/dynamicEditor/templateDataHook";
import { useRepContext, useRepTrainingContext } from "../../../contexts/editableItemContext";
import { Rep } from "../../../models";
import { RepSegment, RepTraining } from "../../../models/repSegment";
import { tabs } from "../../../string";
import { Props } from "../types";

interface IState extends ITemplateData {
	repTraining: RepTraining;
	lists: ITrainingList;
}

function ManualTrainingEditorDynamic(props: Props) {
	const { repId, rep } = useRepContext();
	const context = useRepTrainingContext();
	const repTraining = context.item;
	const lists = useManualTrainingLists();
	const { checkOnboarding } = useRepOnboardingQuery();
	const { saveRepTraining } = useTrainingApi();
	const queryClient = useQueryClient();
	const { useRepSegmentsQuery } = useRepSegmentsApi(repId);
	const { data: repSegments } = useRepSegmentsQuery();

	useEffect(() => {
		if (repSegments) {
			context.overwriteItem({ ...getTrainingFromSegment(repSegments), ...getTrainerFromRep(rep) });
		}
	}, [repSegments]);

	const templateData = useMemo(
		() => ({
			repTraining,
			lists,
		}),
		[repTraining, lists],
	);

	const save = useCallback(async () => {
		const notify = PushProcessingNotification("Saving Training...");
		try {
			await saveRepTraining(repId, repTraining);
			notify.processing("Refreshing Segment...");
			await queryClient.invalidateQueries({ queryKey: [Queries.ONBOARDING_SEGMENTS, repId] });
			notify.processing("Checking Onboarding Status...");
			await checkOnboarding();
			notify.success("Saved!");
			return repTraining;
		} catch (ex) {
			notify.error("Failed to save");
			throw ex;
		}
	}, [repId, repTraining, queryClient, checkOnboarding, saveRepTraining]);

	async function applyBusinessLogic(id: string, value: any, newState: IState) {
		async function update(name: string, value: any) {
			const result = await updateValues(name, value, newState);
			newState = result.newState;
		}
		const clear = async (name: string) => await update(name, null);
		// Custom logic
		switch (id) {
			case "rep.trainerName":
				await update("repTraining.whoConductedTraining", value);
				break;
		}
		return newState as IState;
	}

	const updateValues = useCallback(updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	return (
		<EditableItemDynamicTab
			itemContext={context}
			pageName={tabs.repTraining}
			otherTemplateData={templateData}
			readonly={props.readOnly}
			itemProp="repTraining"
			updateValues={updateValues}
			save={save}
			loaded
		/>
	);
}

export default ManualTrainingEditorDynamic;

function getTrainingFromSegment(segments: RepSegment[]): RepTraining {
	const segment = segments.find((s) => s.trainingDate);

	return {
		trainingDate: holdInUtc(segment?.trainingDate),
		whoConductedTraining: segment?.whoConductedTraining,
	};
}

function getTrainerFromRep(rep: Rep) {
	return {
		trainerId: rep.trainerId,
		trainerName: rep.trainerName,
	};
}

function useManualTrainingLists() {
	const { getTrainers } = useRepApi();
	const [trainers, trainersLoaded] = useTrainerQuery(Queries.TRAINERS, getTrainers);

	const activeLists = useMemo(() => {
		return {
			trainers: getItems(trainers, trainersLoaded),
		} as ITrainingList;
	}, [trainers, trainersLoaded]);

	function getItems(allItems: KeyedArray<FilterItem>, loaded: boolean, forceInclude?: FilterItem) {
		if (!loaded) return null as keyPair[];
		if (forceInclude) allItems.set(forceInclude);
		return allItems.map((c) => {
			return { id: c.id, value: c.name } as keyPair;
		});
	}

	return activeLists;
}

function useTrainerQuery<T extends ActivableFilterItem>(name: string, api: (...params) => Promise<T[]>) {
	const { rep } = useRepContext();
	const [items, loaded] = useFilterItemQuery<T>([name], api, rep.repId, rep.branchId);

	//MSC 5/20 - we used to filter on inactive, but apparently we want to show all trainers in branch,
	//regardless of status
	const filteredItems = useMemo(() => {
		if (!loaded || !items) return emptyArray;
		return items;
	}, [items, loaded]);
	return [filteredItems, loaded] as [KeyedArray<T>, boolean];
}
const emptyArray = new KeyedArray<ActivableFilterItem>([], (k) => k.id);

interface ITrainingList {
	trainers: keyPair[];
}
